import dayjs from "dayjs";
import i18n from "i18n";
import {
  assignTransportToRequest,
  cancelMediaRequest,
  checkCancelRequest,
  checkDiscrepancies,
  checkRequestAuthorization,
  findMediaRequest,
  getAddonCutoffValues,
  getAllCustomers,
  getAllDeletableContainers,
  getAllInsertTypes,
  getAllLockTypes,
  getAllLogicalVaults,
  getAllServiceLocations,
  getAllTripTypes,
  getAuthorizationPersonnels,
  getBranchBillingSystem,
  getEligibleRequestLocations,
  getOpenMediaPossibleMatches,
  getPricingContractFlag,
  getRequiredInsertsCount,
  getScheduledServiceDates,
  getServiceAuthComments,
  loadMediaRequestContainers,
  loadMediaRequestDeleteContainers,
  loadMediaRequestIssueContainers,
  loadMediaRequestOpenMedias,
  loadMediaRequestTransports,
  saveMediaRequest,
  updateMediaRequest,
  validateDeleteContainerNumber,
  validateDeliverMediaContainerNumber,
  validateDeliverMediaOpenMediaNumber
} from "services/common";
import {
  DUPLICATES_TXT,
  EXCHANGE_LOCATION_TYPE_SL,
  myYearMonthDayFormat,
  N_CHECKER,
  POSSIBLE_MATCHES_TXT,
  REQUEST_NEW_ITEM_TYPE,
  TAG_TYPES,
  Y_CHECKER
} from "utils/constants";
import {
  NO_LONGER_TIED_TO_REQUEST,
  SF_ASSIGN_CODE,
  SF_REMOVE_CODE,
  SYSTEM_FUNCTION_ID_SAVE
} from "utils/constants/request-module/MediaRequestDetailConstants";
import {
  changeDateFormatWithoutTime,
  dataDogErrorLog,
  getAuthenticatedUserBranch,
  getAuthenticatedUserId,
  getLanguageId,
  handleRefId
} from "utils/helpers";
import { baseApi } from "../baseApi";

/**
 * @module requestDetailApi request media detail RTK Query endpoints
 */
export const requestDetailApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    checkRequestAuthorization: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await checkRequestAuthorization(props);
          return {
            data: response
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/determineauthorizationrequirement`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              request_type_id: props.requestTypeId,
              request_action_id: props.requestActionId,
              customer_id: props.customerId,
              personnel_id: props.personnelId,
              error: err
            },
            refId
          );
          return { error: message };
        }
      }
    }),
    getAllCustomersWithDetails: builder.query({
      queryFn: async () => {
        try {
          const response = await getAllCustomers();
          return {
            data: response?.map(
              ({
                number,
                name,
                customer_id,
                authorization_comment,
                service_comment,
                district_id
              }) => ({
                label: `${number} - ${name}`,
                value: customer_id,
                number: number,
                authorizationComment: authorization_comment,
                serviceComment: service_comment,
                districtId: district_id
              })
            )
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(message, "", { error: err }, refId);
          return { error: message };
        }
      },
      providesTags: [TAG_TYPES.customerWithDetails]
    }),
    getScheduledServiceDates: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await getScheduledServiceDates(props);
          return {
            data: response
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/returnrequestidandscheduleddateforcustomer`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              customer_id: props.customerId,
              from_date: props.fromDate,
              to_date: props.toDate,
              reschedule_flag: N_CHECKER,
              addon_cutoff_flag: props.addonCutoffFlag,
              error: err
            },
            refId
          );
          return { error: message };
        }
      }
    }),
    getServiceAuthComments: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await getServiceAuthComments(props);
          return {
            data: response
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/getserviceauthcomments`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              customer_id: props.customerId,
              exchange_location_type_code: EXCHANGE_LOCATION_TYPE_SL,
              service_frequency_flag: Y_CHECKER,
              error: err
            },
            refId
          );
          return { error: message };
        }
      }
    }),
    getAddonCutoffValues: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await getAddonCutoffValues(props);
          return {
            data: response
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/getaddoncutoffvaluesforcustomer`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              customer_id: props.customerId,
              error: err
            },
            refId
          );
          return { error: message };
        }
      }
    }),
    getBranchBillingSystem: builder.query({
      queryFn: async () => {
        try {
          const response = await getBranchBillingSystem();
          return {
            data: response
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_BILLING}/status/getcurrentbranchbillingsystem`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              district_id: getAuthenticatedUserBranch(),
              error: err
            },
            refId
          );
          return { error: message };
        }
      }
    }),
    getEligibleRequestLocations: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await getEligibleRequestLocations(props);
          return {
            data: response?.map(({ customer_number, customer_id }) => ({
              label: customer_number,
              value: customer_id
            }))
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/geteligiblereqlocations`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              customer_id: props.customerId,
              day_Of_Week: props.dayOfWeekCode,
              error: err
            },
            refId
          );
          return { error: message };
        }
      }
    }),
    getPricingContractFlag: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await getPricingContractFlag(props);
          return {
            data: response
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/returnspricingcontractitemlist`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              customer_id: props.customerId?.customerId || "",
              error: err
            },
            refId
          );
          return { error: message };
        }
      }
    }),
    getTripTypes: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await getAllTripTypes(props);
          return {
            data: response?.map(({ descr, trip_type_id }) => ({
              label: descr,
              value: trip_type_id
            }))
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/gettriptypesfordistrictandrequesttype`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              customer_id: props.customerId,
              request_type_id: props.requestTypeId,
              error: err
            },
            refId
          );
          return { error: message };
        }
      }
    }),
    getServiceLocations: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await getAllServiceLocations(props);
          return {
            data: response?.map(({ number, customer_id }) => ({
              label: number?.trim(),
              value: customer_id
            }))
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/getservicelocations`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              customer_id: props.customerId,
              error: err
            },
            refId
          );
          return { error: message };
        }
      }
    }),
    getTransportNumbers: builder.query({
      queryFn: async (props) => {
        try {
          const response = [];
          return {
            data: response?.map(({ number }) => number)
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(message, "", { error: err }, refId);
          return { error: message };
        }
      },
      providesTags: (result, error, arg) =>
        result
          ? [{ type: TAG_TYPES.transportNumber, id: arg.customerId }]
          : [TAG_TYPES.transportNumber]
    }),
    getLogicalVaults: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await getAllLogicalVaults(props);
          return {
            data: response?.map(
              ({ code, logical_vault_id, media_program_type_id }) => ({
                label: code,
                value: logical_vault_id,
                mediaProgramTypeId: media_program_type_id
              })
            )
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/getlogicalvaultrecords`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              customer_id: props.customerId,
              media_program_type: props.mediaProgramType,
              error: err
            },
            refId
          );
          return { error: message };
        }
      }
    }),
    getInsertTypes: builder.query({
      queryFn: async (props) => {
        try {
          const response = await getAllInsertTypes({ returnNoneValue: true });
          return {
            data: response
              ?.map(({ descr, insert_type_id }) => ({
                label: descr,
                value: insert_type_id
              }))
              .sort((a, b) => a.label.localeCompare(b.label))
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/getactiveinserttypes`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              return_none_value: props.returnNoneValue ? Y_CHECKER : N_CHECKER,
              language_locale_id: getLanguageId(),
              error: err
            },
            refId
          );
          return { error: message };
        }
      },
      providesTags: [TAG_TYPES.insertType]
    }),
    getLockTypes: builder.query({
      queryFn: async (props) => {
        try {
          const response = await getAllLockTypes(props);
          return {
            data: response
              ?.map(({ lock_type_code }) => ({
                label: lock_type_code,
                value: lock_type_code
              }))
              .sort((a, b) => a.label.localeCompare(b.label))
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/getactivemultimediainserttypes`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              customer_id: props.customerId,
              error: err
            },
            refId
          );
          return { error: message };
        }
      },
      providesTags: (result, error, arg) =>
        result
          ? [{ type: TAG_TYPES.lockType, id: arg.customerId }]
          : [TAG_TYPES.lockType]
    }),
    getAuthorizationPersonnels: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await getAuthorizationPersonnels(props);
          return {
            data: response?.map(({ name, personnel_id }) => ({
              label: name,
              value: personnel_id
            }))
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/authorizedby`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              customer_id: props.customerId,
              error: err
            },
            refId
          );
          return { error: message };
        }
      }
    }),
    validateDeliverMediaOpenMediaNumber: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await validateDeliverMediaOpenMediaNumber(props);
          let data = null;

          if (response) {
            const error = response.errors[0]?.error || 0;
            switch (Number(error)) {
              case 0:
                data = {
                  medias: response.medias.map((media) => {
                    let followUp = "";
                    if (media.follow_up !== "" && media.follow_up_date !== "") {
                      followUp = `${
                        media.follow_up
                      } ${changeDateFormatWithoutTime(
                        media.follow_up_date,
                        props.localeByBranch
                      )}`;
                    } else if (
                      media.follow_up === POSSIBLE_MATCHES_TXT ||
                      media.follow_up === DUPLICATES_TXT ||
                      media.follow_up !== ""
                    ) {
                      followUp = media.follow_up;
                    }

                    return {
                      ...media,
                      follow_up: followUp
                    };
                  })
                };
                break;
              case 64034:
                data = {
                  volsersInMdr: response.volsersInMdr[0].volsers_in_mdr
                };
                break;
              default:
                break;
            }

            return {
              data: data
            };
          } else {
            return {
              data: {
                error: i18n.t(
                  "mediaRequestDetail.unableToProcessBcauseMediaStatusNotFound"
                )
              }
            };
          }
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/validatevolsersinpicklist`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              customer_id: props.customerId,
              volser_list: props.volserList,
              is_ssie_flag: N_CHECKER,
              locale_id: "1",
              language_locale_id: "1",
              at_iron_mtn_status: props.atIronMtnStatus,
              in_transit_status: props.inTransitStatus,
              not_at_iron_mtn_status: props.notAtIronMtnStatus,
              media_not_found_status: props.mediaNotFoundStatus,
              possible_matches_status: POSSIBLE_MATCHES_TXT,
              duplicates_status: DUPLICATES_TXT,
              sent_to_text: props.sentToText,
              find_mdr_flag: Y_CHECKER,
              media_in_mdr_status: props.mediaInMdrStatus,
              media_in_mdr_followup: props.mediaInMdrFollowup,
              error: err
            },
            refId
          );
          return { error: message };
        }
      }
    }),
    getOpenMediaPossibleMatches: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await getOpenMediaPossibleMatches(props);
          return {
            data: response.map(
              ({ volser, media_exchange_status, ...rest }) => ({
                ...rest,
                volser: volser,
                status: media_exchange_status
              })
            )
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/returnsdeuplicatevolserdetails`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              customer_id: props.customerId,
              volser_list: props.volserList,
              selected_volser: props.selectedVolser,
              follow_up: props.follow_up
                ? props.follow_up
                : POSSIBLE_MATCHES_TXT,
              max_rows_returned: props.maxRowsReturned,
              is_ssie_flag: Y_CHECKER,
              language_locale_id: getLanguageId(),
              error: err
            },
            refId
          );
          return { error: message };
        }
      }
    }),
    validateDeliverMediaContainerNumber: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await validateDeliverMediaContainerNumber(props);
          let data = null;

          const { msg_code } = response.message[0];
          if (Number(msg_code) !== 0) {
            if (Number(msg_code) === 80103) {
              data = {
                error: i18n.t("mediaRequestDetail.statusOfContainerIsNotFound")
              };
            } else if (Number(msg_code) === 80116) {
              data = {
                error: i18n.t("mediaRequestDetail.mediaAssignedHighPriority")
              };
            } else {
              if (Number(msg_code) !== 20355) {
                data = {
                  error: i18n.t(
                    "mediaRequestDetail.statusOfContainerIsNotFound"
                  )
                };
              } else {
                // get the container data
                const container = response.container[0];

                data = {
                  error: `${i18n.t("mediaRequestDetail.statusOfContainerIs")} ${
                    container?.exchange_status_type
                  }`
                };
              }
            }
          } else {
            // get the container data
            const container = response.container[0];
            data = {
              id: container?.container_id,
              container_id: container?.container_id,
              container_number: container?.number,
              status: container?.exchange_status_type,
              run_id: container?.run_id
            };
          }

          return {
            data: data
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/getcontainerrecordwithnumbercustomervaultl6`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              customer_id: props.customerId,
              container_number: props.containerNumber,
              secure_sync_flag: N_CHECKER,
              logical_vault_id: props.logicalVaultId,
              media_program_type_id: props.mediaProgramTypeId,
              redelivery_flag: props.redeliveryFlag ? Y_CHECKER : N_CHECKER,
              new_request_type_id: props.newRequestTypeId,
              error: err
            },
            refId
          );
          return { error: message };
        }
      }
    }),
    getRequiredInsertsCount: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await getRequiredInsertsCount(props);
          return {
            data: response[0]?.inserts_required || 0
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(message, "", { error: err }, refId);
          return { error: message };
        }
      }
    }),
    validateDeleteContainerNumber: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await validateDeleteContainerNumber(props);
          let data = null;

          const { err_msg, container_status_text } = response.message[0];
          if (Number(err_msg) !== 0) {
            if (Number(err_msg) !== 20355) {
              data = {
                error: i18n.t(
                  "mediaRequestDetail.unableToProcessBcauseContainerStatusIsNotFound"
                )
              };
            } else {
              data = {
                error: `${i18n.t(
                  "mediaRequestDetail.unableToProcessBcauseContainerStatusIs"
                )} ${container_status_text}}`
              };
            }
          } else {
            data = response.container[0];
          }

          return {
            data: data
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/getallcontainerrecordswithnumbercustomervaultl7`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              customer_id: props.customerId,
              container_number: props.containerNumber,
              secure_sync_flag: props.secureSyncFlag,
              error: err
            },
            refId
          );
          return { error: message };
        }
      }
    }),
    getAllDeletableContainers: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await getAllDeletableContainers(props);
          return {
            data: response.sort((a, b) => {
              // parse numbers to number
              const numA = parseInt(a.number, 10);
              const numB = parseInt(b.number, 10);

              // check whether values are numbers or not
              if (isNaN(numA) && isNaN(numB)) {
                // sort numerically
                return numA - numB;
              } else {
                // sort alphabetically
                return a.number.toString().localeCompare(b.number);
              }
            })
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/getallcontainerrecordswithnumbercustomervaultl8`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              customer_id: props.customerId,
              secure_sync_flag: props.secureSyncFlag,
              error: err
            },
            refId
          );
          return { error: err };
        }
      }
    }),
    saveMediaRequest: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await saveMediaRequest(props);
          return {
            data: response
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/controldatamodificationsforrequest`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              detail_flag: Y_CHECKER,
              request_type_id: String(props.requestTypeId),
              request_action_id: String(props.requestActionId),
              system_function_id: SYSTEM_FUNCTION_ID_SAVE,
              open_media_list: props.openMediaList,
              last_mod_user: "",
              re_run_comment: props.reRunComment,
              regular_rate_special_flag: props.regularRateSpecialFlag
                ? Y_CHECKER
                : N_CHECKER,
              dr_event_id: "",
              dr_site_name: "",
              redelivery_flag: props.redeliveryFlag ? Y_CHECKER : N_CHECKER,
              trip_type_id: String(props.tripTypeId),
              auth_bypass_reason: props.authBypassReason,
              page_number: "",
              no_of_records: "",
              request: [
                {
                  request_type_id: String(props.requestTypeId),
                  request_action_id: String(props.requestActionId),
                  day_of_week_code: props.dayOfWeekCode,
                  exchange_location_type_code: EXCHANGE_LOCATION_TYPE_SL,
                  service_date: dayjs(props.serviceDate).format(
                    myYearMonthDayFormat
                  ),
                  employee_id: getAuthenticatedUserId(),
                  employee_comment: props.ironMountainComment,
                  customer_id: String(props.customerId),
                  personnel_id: String(props.personnelId),
                  personnel_comment: props.customerComment,
                  confirm_personnel_id: props.confirmPersonnelId,
                  comment: props.comment,
                  non_billable_special_flag: props.noChargeSpecialFlag
                    ? Y_CHECKER
                    : N_CHECKER,
                  non_billable_special_comment: props.noChargeSpecialComment,
                  run_position_id: "",
                  request_status_id: String(props.requestStatus),
                  csl_customer_id: Number(props.serviceLocation),
                  parent_request_id: Number(props.parentRequestId)
                }
              ],
              request_media_iu: props.requestMediaIu,
              request_new_item: props.requestNewItem,
              container: props.container,
              error: err
            },
            refId
          );
          return { error: message };
        }
      }
    }),
    updateMediaRequest: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await updateMediaRequest(props);
          return {
            data: response
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/updateanexistingrequest`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              request_id: props.requestId,
              request_status_id: props.requestStatus,
              confirm_personnel_id: props.confirmedBy,
              non_billable_special_comment: props.noChargeSpecialComment,
              non_billable_special_flag: props.noChargeSpecialFlag
                ? Y_CHECKER
                : N_CHECKER,
              employee_comment: props.ironMountainComment,
              personnel_comment: props.customerComment,
              request_ts: props.timestamp,
              secure_sync_flag: N_CHECKER,
              last_mod_user: props.lastModUser,
              re_run_comment: props.reRunComment,
              regular_rate_special_flag: props.regularRateSpecialFlag
                ? Y_CHECKER
                : N_CHECKER,
              trip_type_id: Number(props.tripType) > 0 ? props.tripType : "",
              error: err
            },
            refId
          );
          return { error: message };
        }
      }
    }),
    findMediaRequest: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await findMediaRequest(props);
          return {
            data: response
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/getsinglerequestdata`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              request_id: props.requestId,
              error: err
            },
            refId
          );
          return { error: message };
        }
      }
    }),
    loadMediaRequestOpenMedias: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await loadMediaRequestOpenMedias(props);
          return {
            data: response.map(({ volser, ...rest }) => ({
              ...rest,
              volser,
              id: volser
            }))
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/returnslistofopenmediaofarequest`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              request_id: props.requestId,
              language_locale_id: getLanguageId(),
              error: err
            },
            refId
          );
          return { error: message };
        }
      }
    }),
    loadMediaRequestContainers: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await loadMediaRequestContainers(props);
          return {
            data: response.map((cont) => ({
              id: cont.container_id,
              container_number: cont.number,
              status: cont.exchange_status
            }))
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/returnscontainerinfolistofarequest`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              request_id: props.requestId,
              language_locale_id: getLanguageId(),
              secure_sync_flag: props.secureSyncFlag
                ? props.secureSyncFlag
                : N_CHECKER,
              no_longer_tied_to_request_status:
                props.noLongerTiedToRequestStatus
                  ? props.noLongerTiedToRequestStatus
                  : NO_LONGER_TIED_TO_REQUEST,
              error: err
            },
            refId
          );
          return { error: message };
        }
      }
    }),
    loadMediaRequestTransports: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await loadMediaRequestTransports(props);
          return {
            data: response
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/findtransportsforrequest`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              request_id: props.requestId,
              customer_id: props.customerId,
              error: err
            },
            refId
          );
          return { error: message };
        }
      }
    }),
    loadMediaRequestIssueContainers: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await loadMediaRequestIssueContainers(props);
          let responseData = {
            extraItems: {
              lock_quantity: 0,
              lock_type_code: "",
              clip_quantity: 0
            },
            containers: [],
            extraInserts: []
          };

          let inserts = response.inserts.map(
            ({ request_new_item_id, insert_type_descr, quantity }) => ({
              id: request_new_item_id,
              insert_type: insert_type_descr,
              quantity: quantity
            })
          );

          response.containers.forEach((cont) => {
            // if the row is a type of extra lock add it to the extra lock
            if (
              Number(cont.request_new_item_type_id) ===
              REQUEST_NEW_ITEM_TYPE.LOCKS
            ) {
              responseData.extraItems = {
                ...responseData.extraItems,
                lock_type_code: cont.lock_type_code,
                lock_quantity: Number(cont.quantity)
              };
            }

            // if the row is a type of extra clip add it to the extra clip
            if (
              Number(cont.request_new_item_type_id) ===
              REQUEST_NEW_ITEM_TYPE.CLIPS
            ) {
              responseData.extraItems = {
                ...responseData.extraItems,
                clip_quantity: Number(cont.quantity)
              };
            }

            // if the row is a type of extra insert, set the bill_for_inserts_flag to the appropriate extra insert
            if (
              Number(cont.request_new_item_type_id) ===
              REQUEST_NEW_ITEM_TYPE.INSERT
            ) {
              inserts = inserts.map((insert) => {
                if (Number(insert.id) === Number(cont.request_new_item_id)) {
                  return {
                    ...insert,
                    bill_for_inserts_flag: cont.bill_for_inserts_flag
                  };
                } else {
                  return insert;
                }
              });
            }

            // if the row is a container/transport add it to the container array
            if (
              Number(cont.request_new_item_type_id) ===
                REQUEST_NEW_ITEM_TYPE.CONTAINER ||
              Number(cont.request_new_item_type_id) ===
                REQUEST_NEW_ITEM_TYPE.TRANSPORT_EMPTY ||
              Number(cont.request_new_item_type_id) ===
                REQUEST_NEW_ITEM_TYPE.TRANSPORT_PERMANENT
            ) {
              let insert_configuration = "";
              // remove container/transport insert configurations from the inserts
              inserts = inserts.filter((insert) => {
                if (Number(insert.id) === Number(cont.request_new_item_id)) {
                  insert_configuration += `${insert.quantity} - ${insert.insert_type} `;
                  return false;
                } else {
                  return true;
                }
              });

              responseData.containers.push({ ...cont, insert_configuration });
            }
          });

          // add the extra inserts to the response object
          responseData.extraInserts = inserts;

          return {
            data: responseData
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/returnsallnewitemsofarequest`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              request_id: props.requestId,
              error: err
            },
            refId
          );
          return { error: message };
        }
      }
    }),
    loadMediaRequestDeleteContainers: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await loadMediaRequestDeleteContainers(props);
          return {
            data: response
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/returnscontainerlistofarequest`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              request_id: props.requestId,
              language_locale_id: getLanguageId(),
              secure_sync_flag: props.secureSyncFlag
                ? props.secureSyncFlag
                : N_CHECKER,
              no_longer_tied_to_request_status:
                props.noLongerTiedToRequestStatus
                  ? props.noLongerTiedToRequestStatus
                  : NO_LONGER_TIED_TO_REQUEST,
              error: err
            },
            refId
          );
          return { error: message };
        }
      }
    }),
    assignTransportToRequest: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await assignTransportToRequest(props);
          return {
            data: response
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/assigndistricttransportstocustomer`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              request_id: String(props.requestId),
              employee_id: getAuthenticatedUserId(),
              sf_assign_code: SF_ASSIGN_CODE,
              sf_remove_code: SF_REMOVE_CODE,
              request_transport: props.requestTransports,
              error: err
            },
            refId
          );
          return { error: message };
        }
      }
    }),
    checkCancelRequest: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await checkCancelRequest(props);
          return {
            data: response
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/getnextscheduledreturndate`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              request_id: props.id,
              secure_sync_flag: N_CHECKER,
              language_locale_id: getLanguageId(),
              no_longer_tied_to_request_status: NO_LONGER_TIED_TO_REQUEST,
              error: err
            },
            refId
          );
          return { error: message };
        }
      }
    }),
    cancelMediaRequest: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await cancelMediaRequest(props);
          return {
            data: response
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/processrequestcancellation`,
            {
              main_district_id: getAuthenticatedUserBranch(),
              request_id: props.requestId,
              cancel_personnel_id: props.cancelPersonnelId,
              cancel_employee_id: getAuthenticatedUserId(),
              timestamp: props.timestamp,
              secure_sync_flag: N_CHECKER,
              last_mod_user: "",
              auth_bypass_reason: props.cancelBypassReason,
              is_holiday_pref_cancel_flag: N_CHECKER,
              return_results: "0",
              error: err
            },
            refId
          );
          return { error: message };
        }
      }
    }),
    checkDiscrepancies: builder.mutation({
      queryFn: async (props) => {
        try {
          const response = await checkDiscrepancies(props);
          return {
            data: response
          };
        } catch (err) {
          const { refId, message } = handleRefId(err);
          dataDogErrorLog(
            message,
            `${process.env.REACT_APP_CF_URL_MODULE_REQUEST}/request/checkfordiscrepancies`,
            { error: err },
            refId
          );
          return { error: message };
        }
      }
    })
  })
});
